/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Tag } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import type { SkillProgress } from 'bundles/page-config-common/types/enterprise';
import { generateSkillsPageUrl } from 'bundles/skills-common';

type Props = {
  skill: SkillProgress;
  index: number;
  programSlug: string;
  source: string;
};

const styles = {
  skillTag: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
    border-radius: 24px;
  `,
};

const SkillFocusTag = ({ skill, index, programSlug, source }: Props) => {
  const track = useTracker();
  const { skillId, skillName, score } = skill;
  const skillUrl = generateSkillsPageUrl(skillId, programSlug);

  const eventingV3SkillData = {
    skillCard: {
      index,
      skillScore: score,
    },
    skill: {
      id: skillId,
      name: skillName,
    },
  };

  const skillCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_skill_card',
    eventingV3SkillData
  );

  const handleSkillCardClick = () => {
    track('click_skill_card', eventingV3SkillData);
    redirectClientOrServer(`${skillUrl}?source=${source}`);
  };

  const handleSkillCardKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSkillCardClick();
    }
  };

  return (
    <Grid item ref={skillCardRef}>
      <Tag
        css={styles.skillTag}
        onClick={handleSkillCardClick}
        onKeyDown={handleSkillCardKeypress}
        role="button"
        tabIndex={0}
      >
        {skillName}
      </Tag>
    </Grid>
  );
};

export default SkillFocusTag;
